import * as AdActions from "actions/AdActions";
import * as InactivityActions from "actions/InactivityActions";
import * as PlaybackStateActions from "actions/PlaybackStateActions";
import getAdNetworkOptions from "selectors/ads/getAdNetworkOptions";
import getAdNetworkUrl from "selectors/ads/getAdNetworkUrl";
import getAdPlaying from "selectors/ads/getAdPlaying";
import getAdRequiresDirectUserInteraction from "selectors/ads/getAdRequiresDirectUserInteraction";
import getCanAutoplaySilentVideo from "selectors/device/getCanAutoplaySilentVideo";
import getCanControlVolume from "selectors/device/getCanControlVolume";
import getCanInitiallyAutoplay from "selectors/device/getCanInitiallyAutoplay";
import getCanPlayAds from "selectors/ads/getCanPlayAds";
import getCanPlaySkippableAds from "selectors/ads/getCanPlaySkippableAds";
import getIsiPad from "selectors/device/getIsiPad";
import getPauseVideoOnBlur from "selectors/getPauseVideoOnBlur";
import getPlayAd from "selectors/ads/getPlayAd";
import getPlaybackState from "selectors/chapter/getPlaybackState";
import getRequireVerseThree from "selectors/loading/getRequireVerseThree";
import getUseCanvasVideo from "selectors/device/getUseCanvasVideo";
import getUsePersistentVideo from "selectors/device/getUsePersistentVideo";
import getUserRequestedAdSkip from "selectors/ads/getUserRequestedAdSkip";
import getWaitForDurationChangeToSeek from "selectors/device/getWaitForDurationChangeToSeek";
import getWaitForDataLoadedToSeek from "selectors/device/getWaitForDataLoadedToSeek";
import getWaitingForInitialPlay from "selectors/device/getWaitingForInitialPlay";
import makeGetHotspotClick from "selectors/hotspot/makeGetHotspotClick";
import makeGetMediaSource from "selectors/makeGetMediaSource";
import makeGetPosterUrl from "selectors/video/makeGetPosterUrl";
import makeGetTextTracks from "selectors/captions/makeGetTextTracks";
import makeGetThreeConfig from "selectors/video/makeGetThreeVideoConfig";
import VideoComponent from "components/VideoComponent";
import { AnalyticsActions } from "utils/Analytics";
import { connect } from "react-redux";
import { hideTextTrackCaptionSettings } from "actions/CaptionActions";
import { initialLoadComplete } from "actions/LoadingActions";
import { compose, fromRenderProps, withHandlers } from "recompose";
import makeGetInitialDashBitrate from "selectors/makeGetInitialDashBitrate";
import getIsSafari from "selectors/device/getIsSafari";
import getIsMobileSafari from "selectors/device/getIsMobileSafari";
import getLimitBitrateByPortal from "selectors/device/getLimitBitrateByPortal";
import getReceivedUserInteraction from "selectors/device/getReceivedUserInteraction";
import makeGetSupportsPosterImageCapture from "selectors/video/makeGetSupportsPosterImageCapture";
import { useContext } from "react";
import { ImagePreloaderStatusContext } from "context/ImagePreloaderStatusContext";

import { WithPlaybackRate } from "../context/PlaybackRate";

// const getAdsBlocked = (state: VerseStore, props: {adsBlocked?: ?bool}) => !!props.adsBlocked;

const getMuted = (state, ownProps) => {
  if (getPlayAd(state, ownProps) && !getAdPlaying(state, ownProps)) {
    return true;
  }

  if (typeof ownProps.forceMute === "boolean") {
    return (
      ownProps.forceMute ||
      (state.volume.muted && getCanControlVolume(state, ownProps))
    ); // prevent permanent mute if the build changes
  }

  return state.volume.muted;
};

const makeMapStateToProps = () => {
  const getHotspotClick = makeGetHotspotClick();
  const getMediaSource = makeGetMediaSource();
  const getPosterUrl = makeGetPosterUrl();
  const getTextTracks = makeGetTextTracks();
  const getThreeVideoConfig = makeGetThreeConfig();
  const getInitialDashBitrate = makeGetInitialDashBitrate();
  const getSupportsPosterImageCapture = makeGetSupportsPosterImageCapture();

  const mapStateToProps = (state, props) => {
    const playbackState = getPlaybackState(state, props);
    const video = state.story.entities.videos[props.videoId];
    return {
      adNetworkUrl: getAdNetworkUrl(state, props),
      adNetworkOptions: getAdNetworkOptions(state, props),
      adPlaying: getAdPlaying(state, props),
      adRequiresDirectUserInteraction: getAdRequiresDirectUserInteraction(
        state,
        props,
      ),
      autoplay:
        (!state.playerOptions.designMode && props.autoplay) ||
        (playbackState.playing &&
          !playbackState.userInitiatedPause &&
          props.autoplay) ||
        !!state.playerOptions.autoplay,
      canAutoplaySilentVideo: getCanAutoplaySilentVideo(state, props),
      canPlayAds: getCanPlayAds(state, props),
      canPlaySkippableAds: getCanPlaySkippableAds(state, props),
      canvasVideoUsesAudio: !getIsiPad(state, props),
      debugVideoPlayback: state.playerOptions.debugVideoPlayback,
      forceMute:
        (!getCanInitiallyAutoplay(state) && state.volume.initiallyMuted) ||
        props.forceMute,
      hotspotClick: (hotspotProps) => getHotspotClick(state, hotspotProps),
      initialDashBitrate: getInitialDashBitrate(state, props),
      is360: Boolean(video.is360),
      limitBitrateByPortal: getLimitBitrateByPortal(state, props),
      mediaSource: getMediaSource(state, props),
      muted: getMuted(state, props),
      needsThreeLoadReset:
        getIsSafari(state, props) && !getIsMobileSafari(state, props),
      pauseVideoOnBlur: getPauseVideoOnBlur(state, props),
      playAd: getPlayAd(state, props),
      playing: playbackState.playing,
      playingPassive: getUseCanvasVideo(state, props),
      posterUrl: getPosterUrl(state, props),
      receivedUserInteraction: getReceivedUserInteraction(state, props),
      requireVerseThree: getRequireVerseThree(state, props),
      scrubbing: playbackState.scrubbing,
      seeking: playbackState.seeking,
      showVideoPosters: false, // !video.is360 && getShowVideoPosters(state, props),
      supportsPosterImageCapture: getSupportsPosterImageCapture(state, props),
      textTrackCaptionSettingsVisible:
        state.captions.textTrackCaptionSettingsVisible,
      textTracks: getTextTracks(state, props),
      textTrackLanguage: state.captions.textTrackLanguage,
      threeConfig: getThreeVideoConfig(state, props),
      time: playbackState.time,
      useCanvasVideo: getUseCanvasVideo(state, props),
      userInitiatedPause: playbackState.userInitiatedPause,
      usePersistentVideo: getUsePersistentVideo(state, props),
      userRequestedAdSkip: getUserRequestedAdSkip(state, props),
      waitForDataLoadedToSeek: getWaitForDataLoadedToSeek(state, props),
      waitForDurationChangeToSeek: getWaitForDurationChangeToSeek(state, props),
      waitingForInitialPlay: getWaitingForInitialPlay(state, props),
      windowIsBlurred: state.windowIsBlurred,
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onAdComplete: () => {
    dispatch(InactivityActions.reportActivity());
    dispatch(AdActions.adCompleted());
    if (typeof ownProps.onAdComplete === "function") {
      ownProps.onAdComplete();
    }
  },
  onAdLoaded: () => dispatch(AdActions.setAdLoaded()),
  onAdIsSkipAllowedNowChange: (value) =>
    dispatch(AdActions.setAdSkipAllowedNow(value)),
  onAdIsSkippableChange: (value) => dispatch(AdActions.setAdIsSkippable(value)),
  onAdResumed: () =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, { playing: true }),
    ),
  onAdNotPlaying: () => dispatch(AdActions.setAdPlaying(false)),
  onAdPlaying: () => dispatch(AdActions.setAdPlaying(true)),
  onCutPosterImage: (posterImage) =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, {
        posterImage,
      }),
    ),
  onLoadStart: () => dispatch(initialLoadComplete()),
  onSetAdDuration: (value) => dispatch(AdActions.setAdDuration(value)),
  onPause: () =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, { playing: false }),
    ),
  onPlay: () => {
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, { playing: true }),
    );
    ownProps.onPlay && ownProps.onPlay();
  },
  onTextTrackSettingsClosed: () => dispatch(hideTextTrackCaptionSettings()),
  pausePlaybackState: () =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, { playing: false }),
    ),
  setOrientation: (orientation) =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, { orientation }),
    ),
  onBitrateChanged: () => {
    // AnalyticsActions.bitrateReported({
    //     ...ownProps,
    //     newQuality,
    //     oldQuality,
    // });
  },
  onInitiallyStalled: () =>
    dispatch(
      PlaybackStateActions.changePlaybackState(ownProps, {
        didInitiallyStall: true,
      }),
    ),
});

const connector = connect(
  makeMapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onAdPlaying() {
      ownProps.onSeeked();
      ownProps.onReady();
      dispatchProps.onAdPlaying();
    },
    onAdPaused() {
      dispatchProps.pausePlaybackState();
    },
    onHotspotClicked: (hotspot) => {
      stateProps.hotspotClick({ ...ownProps, ...hotspot });
      dispatchProps.pausePlaybackState();
    },
    onOrientationUpdated: (orientation) => {
      dispatchProps.setOrientation(orientation);
    },
    onHotspotOffered: (hotspot) => {
      AnalyticsActions.hotspotOffered({
        chapterId: ownProps.chapterId,
        target: {
          type: hotspot.target.type,
          target: hotspot.target.target,
          chapterData: {
            id: hotspot.target.chapterId,
          },
        },
        hotspotType: hotspot.hotspotType,
      });
    },
  }),
);

function AttachImagePreloaderStatusContextCallbacks({ children }) {
  const { notifyInitialVideoCanPlayThroughReceived } = useContext(
    ImagePreloaderStatusContext,
  );

  return children({
    notifyInitialVideoCanPlayThroughReceived,
  });
}

export default compose(
  connector,
  withHandlers({
    onOrientationUpdated: (props) => props.onOrientationUpdated,
    onHotspotClicked: (props) => props.onHotspotClicked,
    onAdPaused: (props) => props.onAdPaused,
    onHotspotOffered: (props) => props.onHotspotOffered,
    onAdPlaying: (props) => props.onAdPlaying,
    hotspotClick: (props) => props.hotspotClick,
    onPause: (props) => props.onPause,
  }),
  fromRenderProps(WithPlaybackRate, ({ playbackRate }) => ({
    playbackRate,
  })),
  // While we're really just using an `identity` function here; showing the
  // actual props being passed through makes this more understandable
  fromRenderProps(
    AttachImagePreloaderStatusContextCallbacks,
    ({ notifyInitialVideoCanPlayThroughReceived }) => ({
      notifyInitialVideoCanPlayThroughReceived,
    }),
  ),
)(VideoComponent);
